import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../shared/shared.module';
import { LabelService } from '../../services/label.service';
import {
  LabelAndDescription,
  NameAndWidth,
  PatientDiagnoses,
} from '../../models/patient-model';
import { PatientService } from '../../services/patient.service';
import { Subject, takeUntil } from 'rxjs';
import { ErrorPanelService } from '../../services/error-panel.service';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationPanelComponent } from '../../shared/confirmation-panel/confirmation-panel.component';

@Component({
  selector: 'app-diagnosis',
  standalone: true,
  imports: [
    FormsModule,
    SharedModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './diagnosis.component.html',
  styleUrl: './diagnosis.component.scss',
})
export class DiagnosisComponent {
  @Input() patientId: string = '';
  @Input() diagnoses: PatientDiagnoses[] = [];
  @Input() hasModifyButtonBeenClicked: boolean = false;
  @Input() showNewDiagnosisPanel: boolean = false;
  @Input() isLoading: boolean = false;

  @Output() reloadDiagnoses = new EventEmitter<void>();
  @Output() toggleNewPanel = new EventEmitter<void>();

  displayedColumns: string[] = [
    'code',
    'description',
    'date',
    'notes',
    'actions',
  ];

  showModifyPanel: boolean = false;

  inputBoxesForModifyDiagnosis: LabelAndDescription[] = [];
  inputBoxesForNewDiagnosis: LabelAndDescription[] = [
    { label: 'codeAndDescription', description: '' },
    { label: 'date', description: '' },
    { label: 'notes', description: '' },
  ];

  selectedDiagnosis: PatientDiagnoses = {
    id: '',
    patient_id: '',
    code: '',
    description: '',
    date: '',
    notes: '',
  };

  constructor(
    public labelService: LabelService,
    private patientService: PatientService,
    public errorPanelService: ErrorPanelService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  handleMenuAction(action: string, diagnosis: PatientDiagnoses) {
    switch (action) {
      case 'edit':
        this.toggleModifyPanel(diagnosis);
        break;
      case 'delete':
        this.deleteDiagnosis(diagnosis);
        break;
    }
  }

  toggleModifyPanel(diagnosis?: PatientDiagnoses) {
    if (diagnosis) {
      this.selectedDiagnosis = diagnosis;
      this.inputBoxesForModifyDiagnosis = [
        {
          label: 'codeAndDescription',
          description: diagnosis.code + ' - ' + diagnosis.description,
        },
        { label: 'date', description: diagnosis.date },
        { label: 'notes', description: diagnosis.notes },
      ];
    }
    this.showModifyPanel = !this.showModifyPanel;
  }

  toggleNewDiagnosisPanel() {
    this.inputBoxesForNewDiagnosis = [
      { label: 'codeAndDescription', description: '' },
      { label: 'date', description: '' },
      { label: 'notes', description: '' },
    ];
    this.toggleNewPanel.emit();
  }

  confirmModifyDiagnosis(event: LabelAndDescription[]) {
    // split code and description
    let modifyDiagnoses: LabelAndDescription[] = [];
    event.map((item) => {
      if (item.label === 'codeAndDescription') {
        const code = item.description?.split('-')[0];
        const description = item.description?.split('-')[1];
        modifyDiagnoses.push({
          label: 'code',
          description: code,
        });
        modifyDiagnoses.push({
          label: 'description',
          description: description,
        });
      } else {
        modifyDiagnoses.push(item);
      }
    });
    // transform from array to a json
    var mergedObject = modifyDiagnoses.reduce<{ [key: string]: any }>(
      (acc, current) => {
        acc[current.label] = current.description;
        return acc;
      },
      {}
    );

    mergedObject = {
      patient_id: this.patientId,
      ...mergedObject,
    };

    this.inputBoxesForNewDiagnosis = [
      { label: 'codeAndDescription', description: '' },
      { label: 'date', description: '' },
      { label: 'notes', description: '' },
    ];

    this.patientService
      .putDiagnosis(this.selectedDiagnosis.id, mergedObject)
      .subscribe({
        next: (response) => {
          console.log('Diagnosi modificata');
          this.toggleModifyPanel();
          this.reloadDiagnoses.emit();
        },
        error: (error) => {
          console.error('Errore nel modificare la diagnosi', error);
          this.toggleModifyPanel();
          this.errorPanelService.showErrorSnackBar(
            'Errore nel modificare la diagnosi'
          );
        },
      });
  }

  confirmNewDiagnosis(event: LabelAndDescription[]) {
    // split code and description
    let newDiagnoses: LabelAndDescription[] = [];
    event.map((item) => {
      if (item.label === 'codeAndDescription') {
        const code = item.description?.split('-')[0];
        const description = item.description?.split('-')[1];
        newDiagnoses.push({
          label: 'code',
          description: code,
        });
        newDiagnoses.push({
          label: 'description',
          description: description,
        });
      } else {
        newDiagnoses.push(item);
      }
    });
    // transform from array to a json
    var mergedObject = newDiagnoses.reduce<{ [key: string]: any }>(
      (acc, current) => {
        acc[current.label] = current.description;
        return acc;
      },
      {}
    );

    mergedObject = {
      patient_id: this.patientId,
      ...mergedObject,
    };
    this.patientService.createDiagnoses(mergedObject).subscribe({
      next: (response) => {
        console.log('Diagnosi creata');
        this.diagnoses.push(response);
        this.reloadDiagnoses.emit();
        this.toggleNewDiagnosisPanel();
      },
      error: (error) => {
        console.error('Errore nel creare la diagnosi', error);
        this.toggleNewDiagnosisPanel();
        this.errorPanelService.showErrorSnackBar(
          'Errore nel creare la diagnosi'
        );
      },
    });
  }
  deleteDiagnosis(diagnosis: PatientDiagnoses) {
    const dialogRef = this.dialog.open(ConfirmationPanelComponent, {
      data: {
        title: 'Sei sicuro di voler eliminare questa diagnosi?',
        infoText: "L'operazione è irreversibile",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.patientService.deleteDiagnosis(diagnosis.id).subscribe({
          next: (response) => {
            console.log('Diagnosi eliminata correttamente');
            this.reloadDiagnoses.emit();
          },
          error: (error) => {
            console.error("Errore nell'eliminare la diagnosi", error);
            this.errorPanelService.showErrorSnackBar(
              "Errore nell'eliminare la diagnosi"
            );
          },
        });
      }
    });
  }
}
